'use client';

import { memo, useMemo } from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  alpha,
  Box,
  Typography,
} from '@mui/material';
import { Home, History, AccountBalanceWallet, Support, LocalShipping } from '@mui/icons-material';
import { useThemeColors } from '../context/ThemeContext';
import { TEXT } from '../constants/colors';
import Link from 'next/link';
import { useTranslation } from './useTranslation';

const navItems = [
  { key: 'home', label: 'Home', icon: Home, path: '/home', public: true },
  {
    key: 'history',
    label: 'History',
    icon: History,
    path: '/history',
    requiresCompleteProfile: true,
  },
  {
    key: 'activeParcels',
    label: 'Active',
    icon: LocalShipping,
    path: '/activeParcels',
    emphasized: true,
    requiresCompleteProfile: true,
  },
  {
    key: 'support',
    label: 'Support',
    icon: Support,
    path: '/support',
    public: true,
  },
];

const BottomNav = memo(({ active, onNav, isLoggedIn = false, user = null }) => {
  const themeColors = useThemeColors();
  const { t } = useTranslation();

  // Check if user is business with incomplete profile
  const isBusinessIncomplete =
    user?.userType?.toLowerCase() === 'business' && !user?.businessSetupCompleted;

  // Filter nav items based on login status and business profile completion
  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => {
      // Public items always visible
      if (item.public) return true;

      // Protected items require login
      if (!isLoggedIn) return false;

      // Items requiring complete profile hidden for incomplete business users
      if (item.requiresCompleteProfile && isBusinessIncomplete) return false;

      return true;
    });
  }, [isLoggedIn, isBusinessIncomplete]);

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'fixed',
        bottom: '0.4%',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        width: { xs: '96%', sm: '80%' },
        height: '7vh',
        bgcolor: 'transparent',
        opacity: 1,
        margin: '1% auto',
        borderRadius: 'revert-layer',
        p: '1% 4% 2% 4%',
        boxShadow: 'none',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          height: 70,
          px: { xs: 0.5, sm: 2 },
          py: 1,
        }}
      >
        {visibleNavItems.map((item) => {
          const IconComponent = item.icon;
          const isActive = active === item.key;
          const isEmphasized = item.emphasized;
          const isExternal = item.isExternal;

          const content = (
            <Box
              component="a"
              href={isExternal ? item.path : undefined}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                flex: 1,
                minWidth: 0,
                py: 1,
                textDecoration: 'none',
                '&:hover': { transform: 'translateY(-2px)' },
              }}
            >
              {isActive && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -4,
                    width: isEmphasized ? 40 : 32,
                    height: isEmphasized ? 5 : 4,
                    bgcolor: themeColors.primaryColor,
                    borderRadius: '0 0 4px 4px',
                  }}
                />
              )}

              <Box
                sx={{
                  width: isEmphasized ? 56 : 48,
                  height: isEmphasized ? 36 : 28,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isActive
                    ? `${themeColors.primaryColor}${isEmphasized ? '20' : '12'}`
                    : isEmphasized
                      ? `${themeColors.primaryColor}08`
                      : 'transparent',
                  transition: 'all 0.3s ease',
                  mb: 0.25,
                }}
              >
                <IconComponent
                  sx={{
                    fontSize: isEmphasized ? (isActive ? 34 : 32) : isActive ? 28 : 26,
                    color: isActive
                      ? themeColors.primaryColor
                      : isEmphasized
                        ? themeColors.primaryColor
                        : TEXT.secondary,
                    opacity: isEmphasized && !isActive ? 0.7 : 1,
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontSize: isEmphasized ? '0.75rem' : isActive ? '0.7rem' : '0.65rem',
                  fontWeight: isEmphasized ? 800 : isActive ? 700 : 600,
                  color: isActive
                    ? themeColors.primaryColor
                    : isEmphasized
                      ? themeColors.primaryColor
                      : TEXT.secondary,
                  opacity: isEmphasized && !isActive ? 0.8 : 1,
                }}
              >
                {t(`nav.${item.key === 'activeParcels' ? 'active' : item.key}`)}
              </Typography>
            </Box>
          );

          if (isExternal) {
            return (
              <Box key={item.key} sx={{ flex: 1, display: 'flex' }}>
                {content}
              </Box>
            );
          }

          return (
            <Link key={item.key} href={item.path} prefetch passHref legacyBehavior>
              {content}
            </Link>
          );
        })}
      </Box>
    </Paper>
  );
});

BottomNav.displayName = 'BottomNav';

export { BottomNav };
